import { Routes } from '@angular/router';
import {
  canActivate,
  redirectLoggedInTo,
  redirectUnauthorizedTo,
} from '@angular/fire/auth-guard';
import { RegisterPage } from './pages/auth/register/register.page';
import { LoginPage } from './pages/auth/login/login.page';
import { hasCustomClaimRedirect, isOnboarded } from './util/AuthGuard';

import { HomePage } from './pages/dashboard/home/home.page';
import { PendingApprovalPage } from './pages/auth/pending-approval/pending-approval.page';
import { SynergyDetailPage } from './pages/dashboard/synergies/synergy-detail/synergy-detail.page';

const redirectLoggedInToDashboard = () => redirectLoggedInTo(['/dashboard']);
const redirectUnauthorizedToLogin = () =>
  redirectUnauthorizedTo(['/auth/login']);

const isOnboardedUser = () => isOnboarded('business_hub_assigned', '/auth/login', '/auth/onboarding');
const isVerifiedUser = () => hasCustomClaimRedirect('verified', '/dashboard');
const isAdmin = () => hasCustomClaimRedirect(['business_admin', 'central_admin'], '/not-authorized');

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./pages/dashboard/dashboard.page').then(m => m.DashboardPage),
    children: [
      {
        path: '',
        title: 'Dashboard',
        component: HomePage,
      },
      // ========== PRODUCTS ROUTES ==========
      {
        path: 'products',
        children: [
          {
            path: '',
            title: 'Produkte',
            loadComponent: () => import('./pages/dashboard/shop/products/products.page').then(m => m.ProductsPage)
          },
          {
            path: 'new',
            title: 'Neues Produkt',
            loadComponent: () => import('./pages/dashboard/shop/products/product-detail/product-detail.page').then(m => m.ProductDetailPage)
          },
          {
            path: ':id',
            title: 'Produkt Details',
            loadComponent: () => import('./pages/dashboard/shop/products/product-detail/product-detail.page').then(m => m.ProductDetailPage)
          },
          {
            path: ':id/edit',
            title: 'Produkt bearbeiten',
            loadComponent: () => import('./pages/dashboard/shop/products/product-detail/product-detail.page').then(m => m.ProductDetailPage)
          }
        ]
      },
      // ========== ORDERS ROUTES ==========
      {
        path: 'orders',
        title: 'Bestellungen',
        loadComponent: () => import('./pages/dashboard/shop/orders/orders.page').then(m => m.OrdersPage)
      },
      {
        path: 'orders/:id',
        title: 'Bestellung Details',
        loadComponent: () => import('./pages/dashboard/shop/orders/order-detail/order-detail.page').then(m => m.OrderDetailPage)
      },
      // ========== RETURNS ROUTES ==========
      {
        path: 'returns',
        title: 'Rückgabezentrum',
        loadComponent: () => import('./pages/dashboard/returns/return-center/return-center.page').then(m => m.ReturnCenterPage)
      },
      // ========== SYNERGIES ROUTES ==========
      {
        path: 'synergies',
        loadComponent: () => import('./pages/dashboard/synergies/synergies.page').then(m => m.SynergiesPage)
      },
      {
        path: 'synergies/:type/:id',
        component: SynergyDetailPage
      },
      // ========== IOT ROUTES ==========

      // ========== CHAT ROUTES ==========
      {
        path: 'chat',
        loadComponent: () => import('./pages/chat/chat-split/chat-split.page').then(m => m.ChatSplitPage),
        children: [
          {
            path: 'new',
            loadComponent: () => import('./pages/chat/chat-new/chat-new.page').then(m => m.ChatNewPage)
          },
          {
            path: ':id',
            loadComponent: () => import('./pages/chat/chat-detail/chat-detail.page').then(m => m.ChatDetailPage)
          }
        ]
      },

      // ========== ELEARNING ROUTES ==========
      {
        path: 'elearning',
        loadComponent: () => import('./pages/dashboard/elearning/elearning.page').then(m => m.ElearningPage)
      },
      {
        path: 'elearning/:id',
        loadComponent: () => import('./pages/dashboard/elearning/elearning-detail/elearning-detail.page').then(m => m.ElearningDetailPage)
      },
      // ========== INTELLIGENCE ROUTES ==========
      {
        path: 'intelligence',
        loadComponent: () => import('./pages/dashboard/intelligence/intelligence.page').then(m => m.IntelligencePage)
      },
      // ========== BENEFITS ROUTES ==========
      {
        path: 'benefits',
        loadComponent: () => import('./pages/dashboard/benefits/benefits.page').then(m => m.BenefitsPage)
      },
      // ========== INTERFACES ROUTES ==========
      {
        path: 'interfaces',
        loadComponent: () => import('./pages/dashboard/interfaces/interfaces.page').then(m => m.InterfacesPage)
      },
      {
        path: 'interfaces/new',
        loadComponent: () => import('./pages/dashboard/interfaces/create-interface-connection/create-interface-connection.page').then(m => m.CreateInterfaceConnectionPage)
      },
      {
        path: 'interfaces/:interfaceId',
        loadComponent: () => import('./pages/dashboard/interfaces/interface-detail/interface-detail.page').then(m => m.InterfaceDetailPage)
      },
      {
        path: 'badges',
        loadComponent: () => import('./pages/dashboard/badge-list/badge-list.page').then(m => m.BadgeListPage)
      },
      {
        path: 'badges/:id',
        loadComponent: () => import('./pages/dashboard/badge-list/badge-detail/badge-detail.page').then(m => m.BadgeDetailPage)
      },
      // ========== POSTS ROUTES ==========
      {
        path: 'posts',
        loadComponent: () => import('./pages/dashboard/posts/posts.page').then(m => m.PostPage)
      },
      {
        path: 'feed/stats',
        loadComponent: () => import('./pages/dashboard/posts/stats-dashboard/stats-dashboard.page').then(m => m.StatsDashboardPage)
      },
      {
        path: 'posts/new',
        loadComponent: () => import('./pages/dashboard/posts/new-post/new-post.page').then(m => m.NewPostPage)
      },
      // ========== SHOP ROUTES ==========
      {
        path: 'shop',
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full'
          },
          {
            path: 'overview',
            title: 'Shop Übersicht',
            loadComponent: () => import('./pages/dashboard/shop/shop-overview/shop-overview.page').then(m => m.ShopOverviewPage)
          },
          {
            path: 'settings',
            title: 'Shop Einstellungen',
            loadComponent: () => import('./pages/dashboard/shop/shop-config/shop-config.page').then(m => m.ShopConfigPage)
          },
          // ========== TICKETS ROUTES ==========
          {
            path: 'tickets',
            children: [
              {
                path: '',
                title: 'Tickets',
                loadComponent: () => import('./pages/dashboard/shop/tickets/tickets.page').then(m => m.TicketsPage)
              },
              {
                path: 'new',
                title: 'Neues Ticket',
                loadComponent: () => import('./pages/dashboard/shop/tickets/ticket-detail/ticket-detail.page').then(m => m.TicketDetailPage)
              },
              {
                path: 'customers',
                title: 'Verkaufte Tickets',
                loadComponent: () => import('./pages/dashboard/shop/tickets/customer-tickets/customer-tickets.page').then(m => m.CustomerTicketsPage)
              },
              {
                path: 'scan',
                title: 'Ticket Scanner',
                loadComponent: () => import('./pages/dashboard/shop/tickets/ticket-scanner/ticket-scanner.page').then(m => m.TicketScannerPage)
              },
              {
                path: ':id',
                title: 'Ticket bearbeiten',
                loadComponent: () => import('./pages/dashboard/shop/tickets/ticket-detail/ticket-detail.page').then(m => m.TicketDetailPage)
              }
            ]
          }
        ]
      },
      // ========== SETTINGS ROUTES ==========
      {
        path: 'settings',
        title: 'Einstellungen',
        loadComponent: () => import('./pages/dashboard/settings/settings.page').then(m => m.SettingsPage)
      },

      // ========== ADMIN ROUTES ==========
      {
        path: 'admin/users',
        title: 'Benutzerverwaltung',
        loadComponent: () => import('./pages/admin/admin-users/admin-users.page').then(m => m.AdminUsersPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/benefits',
        title: 'Benefits',
        loadComponent: () => import('./pages/admin/admin-benefits/admin-benefits.page').then(m => m.AdminBenefitsPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/elearning',
        loadComponent: () => import('./pages/admin/admin-elearning/admin-elearning.page').then(m => m.AdminElearningPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/elearning/:id',
        loadComponent: () => import('./pages/admin/admin-elearning/elearning-builder/elearning-builder.page').then(m => m.ElearningBuilderPage),
        ...canActivate(isAdmin)
      },
      {
        path: 'admin/invoice-templates',
        title: 'Rechnungsvorlagen',
        loadComponent: () => import('./pages/admin/admin-invoice-templates/admin-invoice-templates.page').then(m => m.AdminInvoiceTemplatesPage),
        ...canActivate(isAdmin)
      }
    ],
    ...canActivate(isOnboardedUser),
  },
  // ========== AUTH ROUTES ==========
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        component: LoginPage,
      },
      {
        path: 'register',
        component: RegisterPage,
        ...canActivate(redirectLoggedInToDashboard),
      },
      {
        path: 'onboarding',
        loadComponent: () => import('./pages/auth/onboarding/onboarding.page').then(m => m.OnboardingPage),
        ...canActivate(redirectUnauthorizedToLogin),
      },
      {
        path: 'pending-approval',
        component: PendingApprovalPage,
        ...canActivate(redirectUnauthorizedToLogin),
      },
    ],
  },
  // ========== PUBLIC ROUTES ==========
  {
    path: 'agbs',
    loadComponent: () => import('./pages/agbs/agbs.page').then(m => m.AgbsPage)
  },
  {
    path: 'elearning-builder',
    loadComponent: () => import('./pages/admin/admin-elearning/elearning-builder/elearning-builder.page').then(m => m.ElearningBuilderPage)
  }
];